import { V2Table } from '@/common/components';
import { useFormatters } from '../../hooks/useFormatters';
import {
  CONFLICT_CONTACT_REQUIRED_COPY,
  CONFLICT_RESOLUTION_REQUIRED_COPY,
  DUPLICATE_ACTIONS,
  MISSING_ACTIONS,
  formatRemindAt,
  getDuplicateRowKey,
} from '../utils';

function joinList(value) {
  if (!Array.isArray(value) || value.length === 0) return '-';
  return value.join(', ');
}

function PreviewScroll({ children }) {
  return <div className="max-h-[320px] overflow-y-auto">{children}</div>;
}

function PreviewCheckbox({
  checked,
  onChange,
  ariaLabel,
  indeterminate,
  isHeader = false,
}) {
  return (
    <label
      className="relative inline-flex size-4 shrink-0 cursor-pointer items-center justify-center select-none"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={ariaLabel}
        ref={(el) => {
          if (el) el.indeterminate = Boolean(indeterminate);
        }}
        className="absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0"
      />
      <span
        aria-hidden
        className={`pointer-events-none flex size-4 items-center justify-center overflow-hidden rounded-[4px] border transition-colors ${
          checked || indeterminate
            ? 'border-primary bg-primary'
            : isHeader
              ? 'border-white/70 bg-transparent'
              : 'border-[#CCCCCC] bg-white'
        }`}
      >
        {indeterminate ? (
          <span className="block h-[2px] w-2 rounded-full bg-white" />
        ) : (
          <svg
            className={`h-3 w-3 text-white stroke-[3.5] ${
              checked ? 'opacity-100' : 'opacity-0'
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </span>
    </label>
  );
}

function PreviewTable({ title, hint, rows, columns }) {
  if (!rows?.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-[15px] font-semibold text-primary">{title}</h4>
      {hint && <p className="text-sm text-text-secondary">{hint}</p>}
      <PreviewScroll>
        <V2Table
          tight
          columns={columns.map(([header, accessor], index) => ({
            id: `${header}-${index}`,
            label: header,
            defaultWidth: index === columns.length - 1 ? 360 : 140,
            minWidth: 80,
            cell: (row) => accessor(row),
          }))}
          data={rows}
          rowKey={(_, index) => index}
        />
      </PreviewScroll>
    </div>
  );
}

function DuplicateConflictsTable({
  rows,
  selectable = false,
  selectedKeys = [],
  onToggleRow,
  onToggleAll,
  selectionError,
}) {
  const selectedSet = new Set(selectedKeys);
  const selectedCount = rows.filter((_, idx) =>
    selectedSet.has(getDuplicateRowKey(rows[idx], idx)),
  ).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-[15px] font-semibold text-primary">
        Already scheduled
      </h4>
      <p className="text-sm text-text-secondary">
        These contacts already have an upcoming WhatsApp reminder.
      </p>
      <PreviewScroll>
        <V2Table
          tight
          columns={[
            ...(selectable
              ? [
                  {
                    id: 'select',
                    label: '',
                    defaultWidth: 52,
                    minWidth: 52,
                    grow: false,
                    align: 'center',
                    stopPropagation: true,
                    header: () => (
                      <PreviewCheckbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        isHeader
                        ariaLabel="Select all contacts"
                        onChange={onToggleAll}
                      />
                    ),
                    cell: (row, idx) => (
                      <PreviewCheckbox
                        checked={selectedSet.has(getDuplicateRowKey(row, idx))}
                        ariaLabel={`Select ${row.phone || 'contact'}`}
                        onChange={() =>
                          onToggleRow(getDuplicateRowKey(row, idx))
                        }
                      />
                    ),
                  },
                ]
              : []),
            {
              id: 'phone',
              label: 'Phone',
              defaultWidth: 150,
              minWidth: 110,
              cell: (row) => row.phone,
            },
            {
              id: 'contact',
              label: 'Contact',
              defaultWidth: 160,
              minWidth: 110,
              cell: (row) => row.contact_name,
            },
            {
              id: 'existing',
              label: 'Existing',
              defaultWidth: 180,
              minWidth: 120,
              cell: (row) => formatRemindAt(row.existing_remind_at),
            },
            {
              id: 'template',
              label: 'Template',
              defaultWidth: 180,
              minWidth: 120,
              cell: (row) => row.existing_template || '-',
            },
          ]}
          data={rows}
          rowKey={(row, idx) => getDuplicateRowKey(row, idx)}
          onRowClick={
            selectable
              ? (row, idx) => onToggleRow(getDuplicateRowKey(row, idx))
              : undefined
          }
          isRowSelected={
            selectable
              ? (row, idx) => selectedSet.has(getDuplicateRowKey(row, idx))
              : undefined
          }
        />
      </PreviewScroll>
      {selectable && selectionError && (
        <p className="text-sm font-medium text-status-error" role="alert">
          {CONFLICT_CONTACT_REQUIRED_COPY}
        </p>
      )}
    </div>
  );
}

function PreviewStat({ title, value, accent }) {
  const { formatNumber } = useFormatters();
  return (
    <div className="relative flex min-h-[72px] flex-col justify-center rounded-[12px] bg-white px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
      <span
        className="absolute bottom-3 left-0 top-3 w-[4px] rounded-r-full"
        style={{ backgroundColor: accent }}
      />
      <span className="ml-2 text-xs text-text-secondary">{title}</span>
      <span className="ml-2 text-xl font-bold text-text-primary">
        {formatNumber(value ?? 0)}
      </span>
    </div>
  );
}

function ActionCard({ label, description, checked, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
        checked
          ? 'border-primary bg-surface-mint'
          : 'border-border-default bg-white hover:border-border-active'
      }`}
    >
      <p className="text-sm font-semibold text-text-primary">{label}</p>
      {description && (
        <p className="text-xs text-text-secondary mt-0.5">{description}</p>
      )}
    </button>
  );
}

export function ReminderImportPreviewStep({
  previewData,
  duplicateAction,
  onDuplicateActionChange,
  duplicateActionError,
  selectedDuplicateKeys,
  onToggleDuplicateRow,
  onToggleAllDuplicates,
  duplicateContactError,
  missingAction,
  onMissingActionChange,
  channel = 'whatsapp',
}) {
  const missingValues = previewData.missing_values || [];
  const fileDuplicates = previewData.file_duplicates || [];
  const duplicates = previewData.duplicates || [];
  const missing = previewData.missing || [];

  const issuesCount =
    (previewData.missing_values_count ?? missingValues.length) +
    (previewData.file_duplicates_count ?? fileDuplicates.length) +
    (previewData.duplicates_count ?? duplicates.length) +
    (previewData.missing_count ?? missing.length);

  const hasSections =
    missingValues.length > 0 ||
    fileDuplicates.length > 0 ||
    duplicates.length > 0 ||
    missing.length > 0;

  const stats = [
    { title: 'Total', value: previewData.total_rows, accent: '#2F6FED' },
    { title: 'Valid', value: previewData.valid_rows_count, accent: '#50AB3D' },
    { title: 'Issues', value: issuesCount, accent: '#E0435C' },
  ];

  const contactColumnHeader = channel === 'email' ? 'Email' : 'Phone';

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((stat) => (
          <PreviewStat key={stat.title} {...stat} />
        ))}
      </div>

      {!hasSections && (
        <p className="text-sm text-text-secondary">
          {previewData.valid_rows_count ?? 0} rows ready to import.
        </p>
      )}

      <PreviewTable
        title="Missing values"
        hint="These rows are incomplete and will not be imported."
        rows={missingValues}
        columns={[
          ['Row', (r) => r.row_number],
          [contactColumnHeader, (r) => r.phone || '-'],
          ['Missing fields', (r) => joinList(r.missing_fields || r.fields)],
        ]}
      />

      <PreviewTable
        title="Duplicate rows in file"
        hint="This number appears more than once. We will use the last complete row."
        rows={fileDuplicates}
        columns={[
          [contactColumnHeader, (r) => r.phone],
          ['Rows', (r) => joinList(r.row_numbers)],
          ['Occurrences', (r) => r.occurrences ?? r.row_numbers?.length ?? '-'],
        ]}
      />

      {duplicates.length > 0 && (
        <div className="flex flex-col gap-3">
          <DuplicateConflictsTable
            rows={duplicates}
            selectable={duplicateAction === 'skip'}
            selectedKeys={selectedDuplicateKeys}
            onToggleRow={onToggleDuplicateRow}
            onToggleAll={onToggleAllDuplicates}
            selectionError={duplicateContactError}
          />
          {onDuplicateActionChange && (
            <div
              className="flex flex-col gap-2"
              role="radiogroup"
              aria-label="Conflict resolution"
              aria-required="true"
              aria-invalid={duplicateActionError ? 'true' : undefined}
            >
              {DUPLICATE_ACTIONS.map((opt) => (
                <ActionCard
                  key={opt.value}
                  label={opt.label}
                  description={opt.description}
                  checked={duplicateAction === opt.value}
                  onSelect={() => onDuplicateActionChange(opt.value)}
                />
              ))}
              {duplicateActionError && (
                <p
                  className="text-sm font-medium text-status-error"
                  role="alert"
                >
                  {CONFLICT_RESOLUTION_REQUIRED_COPY}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <div className="flex flex-col gap-3">
          <PreviewTable
            title="Missing from previous import"
            hint="These contacts were in the last import but not in this file."
            rows={missing}
            columns={[
              ['Phone', (r) => r.phone],
              ['Contact', (r) => r.contact_name],
              ['Previous', (r) => formatRemindAt(r.previous_remind_at)],
            ]}
          />
          {onMissingActionChange && (
            <div
              className="flex flex-col gap-2"
              role="radiogroup"
              aria-label="Missing contacts action"
            >
              {MISSING_ACTIONS.map((opt) => (
                <ActionCard
                  key={opt.value}
                  label={opt.label}
                  description={opt.description}
                  checked={missingAction === opt.value}
                  onSelect={() => onMissingActionChange(opt.value)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
